// src/hooks/useGamificationConfig.js
// Client-side read of the per-brand gamification config
// (events/{eventId}/gamification/config — own collection so rules can keep it
// member-read / manager-write; meta/* is member-writable via the catch-all).
// Mirrors the functions-side defaults
// for the fields the portal UI needs: wallet gate + cashout settings.
// Missing doc / read error => wallet OFF, so brands that have not opted in
// render exactly the legacy experience.

import { useEffect, useState } from 'react';
import { getDoc } from 'firebase/firestore';
import { eventDoc } from '../event/paths';
import { db } from '../lib/firebase';
import { isIsoDay } from '../utils/gamificationSeason';
import { DEFAULT_LEVELS } from '../utils/gamificationGame';

// Light mirror of the engine normalizer's per-mission validity rule
// (functions/gamificationConfig.js normalizeMission): entries the ENGINE would
// drop must not render as mission cards the host can never progress.
const UI_METRICS = ['seasonPayers', 'birthdayShows', 'boothsBooked', 'signIns', 'activeTeamSize', 'recruitsActivated'];
function isRenderableMission(m) {
  if (!m || typeof m !== 'object' || !m.id) return false;
  if (!UI_METRICS.includes(m.metric)) return false;
  const target = Number(m.target);
  if (!Number.isInteger(target) || target <= 0) return false;
  if (m.rewardType === 'cash') return Number(m.rewardAmount) > 0;
  if (m.rewardType === 'perk') return Boolean(m.perk && m.perk.type);
  return false;
}

const DEFAULTS = {
  wallet: { enabled: false },
  // Admin switch on Program Settings: show the first-week checklist card on
  // the host dashboard. Missing / unreadable => OFF.
  playbook: { firstWeekChecklist: false },
  cashout: { minimum: 100, formsRequired: true, abnFormUrl: '', hobbyFormUrl: '' },
  // Phase 2 (missions/perks). Missing/invalid => missions OFF; the engine-side
  // normalizer (functions/gamificationConfig.js) stays the authority — this
  // client copy only shapes what the UI renders.
  missions: {
    enabled: false,
    seasonStart: '',
    seasonLengthWeeks: 6,
    endowedProgress: true,
    catalog: [],
    tracks: [],
    budget: { seasonPoolCap: 0 }
  },
  referral: { enabled: false, recruitBonus: 20, recruiterBonus: 20, activationPayers: 5 },
  // Phase 3 (game layer). Missing/invalid => game OFF; the engine normalizer
  // stays the authority — this shapes what the UI renders.
  game: {
    enabled: false,
    levels: DEFAULT_LEVELS,
    streak: { shieldCostXp: 150, maxShields: 2, xpMultiplierPerWeek: 0.1, xpMultiplierMax: 2 },
    badges: [],
    seasonPass: { rungs: [], eliteRungs: [], eliteThreshold: 20 },
    leaderboards: { hosts: true, teams: true, rookies: true, rookieWindowDays: 28, rivalView: true },
    statusTiers: { blackCardLevelId: '' },
    perkShop: [],
    plays: { enabled: false, shopCostXp: 0 }
  },
  // Phase 4 (loops). Same fail-closed model.
  quests: { enabled: false, weekly: [], personalized: { enabled: false }, comeback: { enabled: false } },
  predictions: { enabled: false, closeHour: 21, prize: null },
  wheel: { enabled: false, slices: [] },
  social: {
    trackedLinks: false,
    clickXp: 0,
    signupXp: 25,
    brandTags: [],
    ig: { enabled: false },
    tiktok: { enabled: false },
    viralMilestones: []
  },
  contentContest: { enabled: false }
};

function normalizeMissionsForUi(raw) {
  const m = raw && typeof raw === 'object' ? raw : {};
  const budget = m.budget && typeof m.budget === 'object' ? m.budget : {};
  const cap = Number(budget.seasonPoolCap);
  const weeks = Number(m.seasonLengthWeeks);
  const seasonStart = typeof m.seasonStart === 'string' ? m.seasonStart.trim() : '';
  return {
    enabled: m.enabled === true,
    seasonStart: isIsoDay(seasonStart) ? seasonStart : '',
    seasonLengthWeeks: Number.isInteger(weeks) && weeks > 0 ? weeks : DEFAULTS.missions.seasonLengthWeeks,
    endowedProgress: m.endowedProgress !== false,
    catalog: Array.isArray(m.catalog) ? m.catalog.filter(isRenderableMission) : [],
    tracks: Array.isArray(m.tracks) ? m.tracks.filter((x) => x && typeof x === 'object' && x.id) : [],
    budget: { seasonPoolCap: Number.isFinite(cap) && cap >= 0 ? cap : 0 }
  };
}

// Light mirror of the engine's normalizeGame — renderable subsets only.
// Levels: same all-or-nothing rule (a broken ladder falls back to defaults so
// the UI and engine agree on what level a host is).
function normalizeGameForUi(raw) {
  const g = raw && typeof raw === 'object' ? raw : {};
  const streak = g.streak && typeof g.streak === 'object' ? g.streak : {};
  const pass = g.seasonPass && typeof g.seasonPass === 'object' ? g.seasonPass : {};
  const boards = g.leaderboards && typeof g.leaderboards === 'object' ? g.leaderboards : {};
  const tiers = g.statusTiers && typeof g.statusTiers === 'object' ? g.statusTiers : {};

  let levels = DEFAULT_LEVELS;
  if (Array.isArray(g.levels) && g.levels.length) {
    const ok =
      g.levels.every(
        (l, i) =>
          l &&
          typeof l.id === 'string' &&
          l.id &&
          Number.isInteger(Number(l.xp)) &&
          (i === 0 ? Number(l.xp) === 0 : Number(l.xp) > Number(g.levels[i - 1].xp))
      ) && new Set(g.levels.map((l) => l.id)).size === g.levels.length;
    if (ok) levels = g.levels.map((l) => ({ id: l.id, name: l.name || l.id, xp: Number(l.xp) }));
  }

  // Integer XP mirrors the engine's normalizeRung — a decimal-XP rung is
  // DROPPED by the engine (invalidRungIds), so rendering it would show hosts
  // a rung that can never unlock (Codex review 2026-07-05).
  const renderableRung = (r) =>
    r &&
    typeof r === 'object' &&
    r.id &&
    Number.isInteger(Number(r.xp)) &&
    Number(r.xp) > 0 &&
    (r.rewardType === 'cash' ? Number(r.rewardAmount) > 0 : Boolean(r.perk && r.perk.type));
  const renderableBadge = (b) => b && typeof b === 'object' && b.id && b.metric && Number(b.threshold) > 0;
  const renderableShopItem = (i) =>
    i && typeof i === 'object' && i.id && Number.isInteger(Number(i.costXp)) && Number(i.costXp) > 0 && i.perk && i.perk.type;
  const num = (v, fallback) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : fallback);

  return {
    enabled: g.enabled === true,
    levels,
    streak: {
      shieldCostXp: num(streak.shieldCostXp, DEFAULTS.game.streak.shieldCostXp),
      maxShields:
        Number.isInteger(Number(streak.maxShields)) && Number(streak.maxShields) >= 0
          ? Number(streak.maxShields)
          : DEFAULTS.game.streak.maxShields,
      xpMultiplierPerWeek: Number.isFinite(Number(streak.xpMultiplierPerWeek))
        ? Number(streak.xpMultiplierPerWeek)
        : DEFAULTS.game.streak.xpMultiplierPerWeek,
      xpMultiplierMax: num(streak.xpMultiplierMax, DEFAULTS.game.streak.xpMultiplierMax)
    },
    badges: Array.isArray(g.badges) ? g.badges.filter(renderableBadge) : [],
    seasonPass: {
      rungs: Array.isArray(pass.rungs) ? pass.rungs.filter(renderableRung) : [],
      eliteRungs: Array.isArray(pass.eliteRungs) ? pass.eliteRungs.filter(renderableRung) : [],
      eliteThreshold: num(pass.eliteThreshold, DEFAULTS.game.seasonPass.eliteThreshold)
    },
    leaderboards: {
      hosts: boards.hosts !== false,
      teams: boards.teams !== false,
      rookies: boards.rookies !== false,
      rookieWindowDays: num(boards.rookieWindowDays, DEFAULTS.game.leaderboards.rookieWindowDays),
      rivalView: boards.rivalView !== false
    },
    statusTiers: { blackCardLevelId: typeof tiers.blackCardLevelId === 'string' ? tiers.blackCardLevelId : '' },
    perkShop: Array.isArray(g.perkShop)
      ? g.perkShop.filter(renderableShopItem).map((i) => ({
          id: i.id,
          title: i.title || i.id,
          costXp: Number(i.costXp),
          perk: {
            type: i.perk.type,
            label: i.perk.label || '',
            value: Number(i.perk.value) || 1,
            expiryDays: Number(i.perk.expiryDays) || 14
          }
        }))
      : [],
    plays: {
      enabled: Boolean(g.plays && g.plays.enabled === true && Array.isArray(g.plays.rotation) && g.plays.rotation.length),
      shopCostXp: Number(g.plays && g.plays.shopCostXp) || 0
    }
  };
}

// Phase 4 UI mirrors (renderable subsets; engine normalizer is authority).
function normalizeQuestsForUi(raw) {
  const q = raw && typeof raw === 'object' ? raw : {};
  const personalized = q.personalized && typeof q.personalized === 'object' ? q.personalized : {};
  const comeback = q.comeback && typeof q.comeback === 'object' ? q.comeback : {};
  const renderable = (w) =>
    w && typeof w === 'object' && w.id && w.metric && Number(w.target) > 0 && w.reward && typeof w.reward === 'object';
  return {
    enabled: q.enabled === true,
    weekly: Array.isArray(q.weekly) ? q.weekly.filter(renderable) : [],
    personalized: { enabled: personalized.enabled === true },
    comeback: { enabled: comeback.enabled === true }
  };
}

function normalizePredictionsForUi(raw) {
  const p = raw && typeof raw === 'object' ? raw : {};
  const hour = Number(p.closeHour);
  const prize = p.prize && typeof p.prize === 'object' && p.prize.type ? p.prize : null;
  return {
    enabled: p.enabled === true && Boolean(prize),
    closeHour: Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : DEFAULTS.predictions.closeHour,
    prize
  };
}

function normalizeWheelForUi(raw) {
  const w = raw && typeof raw === 'object' ? raw : {};
  const slices = Array.isArray(w.slices) ? w.slices.filter((s) => s && typeof s === 'object' && s.type && Number(s.weight) > 0) : [];
  return { enabled: w.enabled === true && slices.length > 0, slices };
}

function normalizeSocialForUi(raw) {
  const s = raw && typeof raw === 'object' ? raw : {};
  const ig = s.ig && typeof s.ig === 'object' ? s.ig : {};
  const tiktok = s.tiktok && typeof s.tiktok === 'object' ? s.tiktok : {};
  const tags = (Array.isArray(s.brandTags) ? s.brandTags : [])
    .map((t) =>
      String(t || '')
        .trim()
        .toLowerCase()
    )
    .filter(Boolean);
  return {
    trackedLinks: s.trackedLinks === true,
    clickXp: Number(s.clickXp) || 0,
    signupXp: Number(s.signupXp) || 0,
    // Phase 5 (API-first): tiktok mirrors the engine's tag gate — enabled
    // with no brand tags discovers nothing, so the UI treats it as off too.
    brandTags: tags,
    ig: { enabled: ig.enabled === true, storyXp: Number(ig.storyXp) || 0, postXp: Number(ig.postXp) || 0 },
    tiktok: { enabled: tiktok.enabled === true && tags.length > 0, postXp: Number(tiktok.postXp) || 0 },
    viralMilestones: (Array.isArray(s.viralMilestones) ? s.viralMilestones : [])
      .filter((m) => m && Number(m.views) > 0 && m.reward)
      .sort((a, b) => Number(a.views) - Number(b.views))
  };
}

function normalizeReferralForUi(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const num = (v, fallback) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : fallback);
  return {
    enabled: r.enabled === true,
    recruitBonus: num(r.recruitBonus, DEFAULTS.referral.recruitBonus),
    recruiterBonus: num(r.recruiterBonus, DEFAULTS.referral.recruiterBonus),
    activationPayers: num(r.activationPayers, DEFAULTS.referral.activationPayers)
  };
}

export default function useGamificationConfig() {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(eventDoc(db, 'gamification', 'config'));
        const data = snap.exists() ? snap.data() || {} : {};
        const wallet = data.wallet && typeof data.wallet === 'object' ? data.wallet : {};
        const cashout = data.cashout && typeof data.cashout === 'object' ? data.cashout : {};
        const minimum = Number(cashout.minimum);
        if (!cancelled) {
          setConfig({
            wallet: { enabled: wallet.enabled === true },
            cashout: {
              minimum: Number.isFinite(minimum) && minimum >= 0 ? minimum : DEFAULTS.cashout.minimum,
              formsRequired: cashout.formsRequired !== false,
              abnFormUrl: typeof cashout.abnFormUrl === 'string' ? cashout.abnFormUrl : '',
              hobbyFormUrl: typeof cashout.hobbyFormUrl === 'string' ? cashout.hobbyFormUrl : ''
            },
            missions: normalizeMissionsForUi(data.missions),
            referral: normalizeReferralForUi(data.referral),
            game: normalizeGameForUi(data.game),
            quests: normalizeQuestsForUi(data.quests),
            predictions: normalizePredictionsForUi(data.predictions),
            wheel: normalizeWheelForUi(data.wheel),
            social: normalizeSocialForUi(data.social),
            contentContest: { enabled: Boolean(data.contentContest && data.contentContest.enabled === true) },
            playbook: { firstWeekChecklist: Boolean(data.playbook && data.playbook.firstWeekChecklist === true) }
          });
        }
      } catch (err) {
        console.warn('gamification config unavailable; wallet features off', err);
        if (!cancelled) setConfig(DEFAULTS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { config, loading };
}
